import { hasPermission, IPermissionsProps, isVisibleByBranding } from '../../../../helpers/PermissionsHelper';
import { MENU_LINKS } from '../../../../config/routes';
import { RoutePermissions } from '../../../../constants/RoutePermissions';
import { DefaultRoutePermissions } from '../../../../constants/DefaultRoutePermissions';
import { getHomeScreenUrl } from '../../../../helpers/homeScreenUrlHelper';
import { BrandingHiddenSections } from '../../../../main/constants/BrandingHiddenSections';

export const SIDE_MENU_URL_LOGO = {
    BLUE_LINK: 'https://logos.signaturit.com/_.blue/284x100',
    WHITE_LINK: 'https://logos.signaturit.com/_.white/284x100',
    HREF: MENU_LINKS.REQUEST_LIST.PROGRESS.LINK,
};

export interface ISideMenuConfigProps extends IPermissionsProps {
    hiddenSections: BrandingHiddenSections[];
}

export enum SideItemMenuSectionsName {
    REQUEST = 'default.menus._left.documents',
    ALL = 'default.menus._left.all',
    PROGRESS = 'default.menus._left.in_progress',
    ARCHIVED = 'default.menus._left.archived',
    COMPLETED = 'default.menus._left.completed',
    CERTIFICATES = 'default.menus._left.certificates',
    TEMPLATES = 'default.menus._left.templates',
    FILES = 'default.menus._left.certified_files',
    DEVELOPERS = 'default.menus._left.developers',
    API = 'default.menus._left.api_request',
    HOOKS = 'default.menus._left.web_hooks',
    TEAMS = 'default.menus._left.team',
    MEMBERS = 'default.menus._left.members',
    GROUPS = 'default.menus._left.groups',
    CONTACTS = 'default.menus._left.contacts',
    RESELLER = 'default.menus._left.home',
    HOME = 'default.menus._left.home',
}

export enum SideItemMenuIcons {
    REQUEST = 'icon-menu-history',
    CERTIFICATES = 'icon-qualified-certificate',
    TEMPLATES = 'icon-menu-templates',
    FILES = 'icon-certified-file',
    DEVELOPERS = 'icon-menu-developers',
    TEAMS = 'icon-menu-team',
    CONTACTS = 'icon-menu-contacts',
    RESELLER = 'icon-action-home',
    HOME = 'icon-action-home',
}

export interface ISideNavigationMenuItemsConfig {
    baseLink: string;
    link: string;
    fallbackLink?: string;
    name: string;
    subItems: ISideNavigationMenuItemsConfig[];
    visible: boolean;
    icon?: string;
    events?: any[];
    default?: boolean;
    showTag?: boolean;
    withDivider?: boolean;
    bullet?: SideNavigationMenuItemsBulletEnum;
}

export enum SideNavigationMenuItemsBulletEnum {
    BASE = 'base',
    COMPLETED = 'completed',
    PROGRESS = 'progress',
    EMPTY = 'empty',
}

export const sideMenuConfigurationItems = (
    sideMenuConfigProps: ISideMenuConfigProps,
    sectionsWithTag: string[] = []
) => {
    return [
        {
            baseLink: getHomeScreenUrl(sideMenuConfigProps.user?.homeScreen),
            link: getHomeScreenUrl(sideMenuConfigProps.user?.homeScreen),
            icon: SideItemMenuIcons.HOME,
            name: SideItemMenuSectionsName.HOME,
            subItems: [],
            showTag: sectionsWithTag.includes(SideItemMenuSectionsName.HOME),
            visible: hasPermission(sideMenuConfigProps, RoutePermissions.ShowHomeSection),
        },
        {
            baseLink: MENU_LINKS.REQUEST_LIST.BASE,
            link: MENU_LINKS.REQUEST_LIST.BASE,
            icon: SideItemMenuIcons.REQUEST,
            name: SideItemMenuSectionsName.REQUEST,
            default: true,
            visible: hasPermission(sideMenuConfigProps, DefaultRoutePermissions.RequestList),
            subItems: [
                {
                    baseLink: MENU_LINKS.REQUEST_LIST.PROGRESS.BASE,
                    link: MENU_LINKS.REQUEST_LIST.PROGRESS.LINK,
                    name: SideItemMenuSectionsName.PROGRESS,
                    bullet: SideNavigationMenuItemsBulletEnum.PROGRESS,
                    subItems: [],
                    visible: true,
                },
                {
                    baseLink: MENU_LINKS.REQUEST_LIST.COMPLETED.BASE,
                    link: MENU_LINKS.REQUEST_LIST.COMPLETED.LINK,
                    name: SideItemMenuSectionsName.COMPLETED,
                    bullet: SideNavigationMenuItemsBulletEnum.COMPLETED,
                    subItems: [],
                    visible: true,
                },
                {
                    baseLink: MENU_LINKS.REQUEST_LIST.ALL.BASE,
                    link: MENU_LINKS.REQUEST_LIST.ALL.LINK,
                    name: SideItemMenuSectionsName.ALL,
                    default: true,
                    bullet: SideNavigationMenuItemsBulletEnum.BASE,
                    subItems: [],
                    visible: true,
                },
                {
                    baseLink: MENU_LINKS.REQUEST_LIST.ARCHIVED.BASE,
                    link: MENU_LINKS.REQUEST_LIST.ARCHIVED.LINK,
                    name: SideItemMenuSectionsName.ARCHIVED,
                    bullet: SideNavigationMenuItemsBulletEnum.EMPTY,
                    subItems: [],
                    visible: true,
                },
            ],
        },
        {
            baseLink: MENU_LINKS.TEMPLATE_LIST.BASE,
            link: MENU_LINKS.TEMPLATE_LIST.BASE,
            icon: SideItemMenuIcons.TEMPLATES,
            name: SideItemMenuSectionsName.TEMPLATES,
            subItems: [],
            visible: hasPermission(sideMenuConfigProps, DefaultRoutePermissions.Templates),
        },
        {
            baseLink: MENU_LINKS.CERTIFICATES.BASE,
            link: MENU_LINKS.CERTIFICATES.BASE,
            icon: SideItemMenuIcons.CERTIFICATES,
            name: SideItemMenuSectionsName.CERTIFICATES,
            subItems: [],
            visible: hasPermission(sideMenuConfigProps, RoutePermissions.CertificateSection),
        },
        {
            baseLink: MENU_LINKS.FILES.BASE,
            link: MENU_LINKS.FILES.BASE,
            icon: SideItemMenuIcons.FILES,
            name: SideItemMenuSectionsName.FILES,
            subItems: [],
            visible:
                hasPermission(sideMenuConfigProps, DefaultRoutePermissions.Files) &&
                isVisibleByBranding(sideMenuConfigProps.hiddenSections, BrandingHiddenSections.Files),
        },
        {
            baseLink: MENU_LINKS.TEAMS.BASE,
            link: MENU_LINKS.TEAMS.BASE,
            icon: SideItemMenuIcons.TEAMS,
            name: SideItemMenuSectionsName.TEAMS,
            visible: hasPermission(sideMenuConfigProps, DefaultRoutePermissions.Teams),
            subItems: [
                {
                    baseLink: MENU_LINKS.TEAMS.MEMBERS.BASE,
                    link: MENU_LINKS.TEAMS.MEMBERS.LINK,
                    name: SideItemMenuSectionsName.MEMBERS,
                    subItems: [],
                    visible: true,
                },
                {
                    baseLink: MENU_LINKS.TEAMS.GROUPS.BASE,
                    link: MENU_LINKS.TEAMS.GROUPS.LINK,
                    name: SideItemMenuSectionsName.GROUPS,
                    subItems: [],
                    visible: true,
                },
            ],
        },
        {
            baseLink: MENU_LINKS.CONTACT_LIST.BASE,
            link: MENU_LINKS.CONTACT_LIST.BASE,
            icon: SideItemMenuIcons.CONTACTS,
            name: SideItemMenuSectionsName.CONTACTS,
            withDivider: hasPermission(sideMenuConfigProps, RoutePermissions.HasApi),
            subItems: [],
            visible: hasPermission(sideMenuConfigProps, DefaultRoutePermissions.Contacts),
        },
        {
            baseLink: MENU_LINKS.ACCOUNT_DEV.BASE,
            link: MENU_LINKS.ACCOUNT_DEV.BASE,
            icon: SideItemMenuIcons.DEVELOPERS,
            name: SideItemMenuSectionsName.DEVELOPERS,
            visible:
                hasPermission(sideMenuConfigProps, RoutePermissions.HasApi) &&
                isVisibleByBranding(sideMenuConfigProps.hiddenSections, BrandingHiddenSections.Developers),
            subItems: [
                {
                    baseLink: MENU_LINKS.ACCOUNT_DEV.API_REQUEST.BASE,
                    link: MENU_LINKS.ACCOUNT_DEV.API_REQUEST.LINK,
                    name: SideItemMenuSectionsName.API,
                    subItems: [],
                    visible: isVisibleByBranding(sideMenuConfigProps.hiddenSections, BrandingHiddenSections.Developers),
                },
                {
                    baseLink: MENU_LINKS.ACCOUNT_DEV.WEB_HOOK.BASE,
                    link: MENU_LINKS.ACCOUNT_DEV.WEB_HOOK.LINK,
                    name: SideItemMenuSectionsName.HOOKS,
                    subItems: [],
                    visible: isVisibleByBranding(sideMenuConfigProps.hiddenSections, BrandingHiddenSections.Developers),
                },
            ],
        },
        {
            baseLink: MENU_LINKS.RESELLER.BASE,
            link: MENU_LINKS.RESELLER.BASE,
            icon: SideItemMenuIcons.RESELLER,
            name: SideItemMenuSectionsName.RESELLER,
            subItems: [],
            visible: hasPermission(sideMenuConfigProps, RoutePermissions.Reseller),
        },
    ];
};
